import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Shield, Clock, Award, Users, Zap, HeartHandshake } from 'lucide-react';

const WhyChooseContainer = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  margin: 80px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, #00ff88 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, #ff0080 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, #8000ff 0%, transparent 50%);
    opacity: 0.1;
    z-index: 1;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  background: linear-gradient(135deg, #00ff88 0%, #00d4ff 50%, #ff0080 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 20px;
  font-weight: 700;
  text-shadow: 0 0 30px rgba(0, 255, 136, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #a0a9c0;
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
`;

const FeatureCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 255, 136, 0.2);
  padding: 40px 30px;
  border-radius: 20px;
  text-align: center;
  transition: none;
  position: relative;
  overflow: hidden;
  will-change: transform, opacity;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 136, 0.1), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    border: 2px solid #00ff88;
    background: linear-gradient(135deg, rgba(0, 255, 136, 0.15) 0%, rgba(255, 0, 128, 0.15) 50%, rgba(128, 0, 255, 0.15) 100%);
    box-shadow: 
      0 20px 40px rgba(0, 255, 136, 0.3),
      0 0 30px rgba(255, 0, 128, 0.2),
      inset 0 0 20px rgba(0, 255, 136, 0.1);
    
    &::before {
      left: 100%;
    }
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ff0080 0%, #8000ff 50%, #00ff88 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  color: white;
  box-shadow: 0 0 30px rgba(255, 0, 128, 0.4);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: 2px;
    background: linear-gradient(135deg, #1a1a2e, #0f0f23);
    border-radius: 50%;
    z-index: -1;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 15px;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  color: #a0a9c0;
  line-height: 1.6;
`;

const WhyChooseUs = () => {
  const features = [
    {
      icon: <Shield size={32} />,
      title: "Trusted & Secure",
      description: "Enterprise-grade security with 99.9% uptime guarantee. Your data and projects are always protected."
    },
    {
      icon: <Clock size={32} />,
      title: "Fast Delivery",
      description: "Quick turnaround times without compromising quality. We deliver projects on time, every time."
    },
    {
      icon: <Award size={32} />,
      title: "Award Winning",
      description: "Recognized industry leader with multiple awards for excellence in design and customer satisfaction."
    },
    {
      icon: <Users size={32} />,
      title: "Expert Team",
      description: "Skilled professionals with 10+ years of experience in design, development, and digital strategy."
    },
    {
      icon: <Zap size={32} />,
      title: "Cutting Edge",
      description: "Latest technologies and modern approaches to keep your business ahead of the competition."
    },
    {
      icon: <HeartHandshake size={32} />,
      title: "24/7 Support",
      description: "Round-the-clock customer support to help you whenever you need assistance or guidance."
    }
  ];

  return (
    <WhyChooseContainer>
      <Container>
        <Title>Why Choose InitX?</Title>
        <Subtitle>
          We combine expertise, innovation, and dedication to deliver exceptional results for your business
        </Subtitle>
        
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, ease: "linear", delay: index * 0.05 }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Container>
    </WhyChooseContainer>
  );
};

export default WhyChooseUs;